import React, { useEffect, useState, useCallback } from 'react';
import { Offcanvas } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { fetchAuditDetail } from './api';
import { calculateCost, INR_RATE } from './constants';

const CostAnalysis = ({ detail }) => {
    if (!detail?.aiRawResponse?.usageMetadata) return null;

    const { modelVersion } = detail.aiRawResponse;
    const { promptTokenCount, candidatesTokenCount, totalTokenCount } = detail.aiRawResponse.usageMetadata;

    // Import calculateCost from constants
    const { inputCost, outputCost, totalCost, pricing, modelName } = calculateCost(modelVersion, promptTokenCount, candidatesTokenCount);

    const formatCost = (val) => {
        const usd = `$${val.toFixed(6)}`;
        const inr = `₹${(val * INR_RATE).toFixed(4)}`;
        return { usd, inr };
    };

    const formatLargeCost = (val) => {
        const usd = `$${val.toFixed(2)}`;
        const inr = `₹${(val * INR_RATE).toFixed(2)}`;
        return { usd, inr };
    };

    return (
        <div style={{ marginBottom: '32px' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px'
            }}>
                <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#2563eb',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600'
                }}>
                    💰
                </div>
                <h6 style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#334155',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    Cost & Performance Analysis
                </h6>
            </div>

            <div style={{
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                padding: '24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
                {/* 1. Model Info */}
                <div style={{ marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '8px' }}>
                        Model Information
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{modelName}</span>
                            <span style={{
                                marginLeft: '8px',
                                fontSize: '12px',
                                backgroundColor: '#e0e7ff',
                                color: '#4338ca',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontFamily: 'monospace'
                            }}>
                                {modelVersion}
                            </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Official Pricing (per 1M)</div>
                            <div style={{ fontSize: '13px', color: '#334155', fontWeight: '500' }}>
                                Input: ${pricing.input} | Output: ${pricing.output}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Token Breakdown Table */}
                <div style={{ marginBottom: '24px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                <th style={{ padding: '8px', color: '#64748b' }}>Metric</th>
                                <th style={{ padding: '8px', color: '#64748b' }}>Count</th>
                                <th style={{ padding: '8px', color: '#64748b' }}>Cost Calculation</th>
                                <th style={{ padding: '8px', color: '#64748b', textAlign: 'right' }}>Total (USD)</th>
                                <th style={{ padding: '8px', color: '#64748b', textAlign: 'right' }}>Total (INR)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '8px', fontWeight: '500', color: '#334155' }}>Input (Prompt)</td>
                                <td style={{ padding: '8px', color: '#475569', fontFamily: 'monospace' }}>{promptTokenCount}</td>
                                <td style={{ padding: '8px', color: '#94a3b8', fontSize: '12px' }}>
                                    ({promptTokenCount} / 1M) × ${pricing.input}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#334155' }}>
                                    {formatCost(inputCost).usd}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#166534' }}>
                                    {formatCost(inputCost).inr}
                                </td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '8px', fontWeight: '500', color: '#334155' }}>Output (Response)</td>
                                <td style={{ padding: '8px', color: '#475569', fontFamily: 'monospace' }}>{candidatesTokenCount}</td>
                                <td style={{ padding: '8px', color: '#94a3b8', fontSize: '12px' }}>
                                    ({candidatesTokenCount} / 1M) × ${pricing.output}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#334155' }}>
                                    {formatCost(outputCost).usd}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#166534' }}>
                                    {formatCost(outputCost).inr}
                                </td>
                            </tr>
                            <tr style={{ backgroundColor: '#eff6ff' }}>
                                <td style={{ padding: '8px', fontWeight: '700', color: '#1e40af' }}>TOTAL</td>
                                <td style={{ padding: '8px', fontWeight: '700', color: '#1e40af', fontFamily: 'monospace' }}>{totalTokenCount}</td>
                                <td style={{ padding: '8px' }}></td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '800', color: '#1e40af', fontSize: '14px' }}>
                                    {formatCost(totalCost).usd}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '800', color: '#15803d', fontSize: '14px' }}>
                                    {formatCost(totalCost).inr}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* 3. Scaling & Insights */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    {/* Scaling */}
                    <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>📈</span> Scaling Projection
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                <span style={{ color: '#64748b' }}>1,000 reqs:</span>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{ fontWeight: '600', color: '#334155', display: 'block' }}>{formatLargeCost(totalCost * 1000).usd}</span>
                                    <span style={{ fontSize: '11px', color: '#166534' }}>{formatLargeCost(totalCost * 1000).inr}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                <span style={{ color: '#64748b' }}>100,000 reqs:</span>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{ fontWeight: '600', color: '#334155', display: 'block' }}>{formatLargeCost(totalCost * 100000).usd}</span>
                                    <span style={{ fontSize: '11px', color: '#166534' }}>{formatLargeCost(totalCost * 100000).inr}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', paddingTop: '8px', borderTop: '1px dashed #e2e8f0' }}>
                                <span style={{ color: '#64748b' }}>1 Million reqs:</span>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{ fontWeight: '700', color: '#0f172a', display: 'block' }}>{formatLargeCost(totalCost * 1000000).usd}</span>
                                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#15803d' }}>{formatLargeCost(totalCost * 1000000).inr}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recommendation */}
                    <div style={{ backgroundColor: '#f0fdf4', padding: '16px', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontSize: '12px', color: '#166534', textTransform: 'uppercase', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>💡</span> FinOps Recommendation
                        </div>
                        <p style={{ fontSize: '13px', color: '#166534', margin: 0, lineHeight: '1.5' }}>
                            {modelName.includes('Flash') ? (
                                <>
                                    <strong>Excellent Efficiency!</strong> You are using a cost-optimized model. This is ~16x cheaper than the Pro variant. Suitable for high-volume daily health checks.
                                </>
                            ) : (
                                <>
                                    <strong>Consider Optimization.</strong> You are using a Pro model. For routine status checks, consider switching to <strong>Flash</strong> to reduce costs by ~93%.
                                </>
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AuditDetailDrawer = ({ show, onHide, recordId }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copyStatus, setCopyStatus] = useState({ prompt: false, insights: false, raw: false });

    useEffect(() => {
        const loadDetail = async () => {
            setLoading(true);
            setError(null);
            setCopyStatus({ prompt: false, insights: false, raw: false });
            const res = await fetchAuditDetail(recordId);
            if (res.success) {
                setDetail(res.data);
            } else {
                setError(res.message);
            }
            setLoading(false);
        };

        if (show && recordId) {
            loadDetail();
        } else {
            setDetail(null); // Reset on close
            setCopyStatus({ prompt: false, insights: false, raw: false });
        }
    }, [recordId, show]);

    const handleCopy = useCallback(async (text, type) => {
        try {
            let contentToCopy = text;

            // Handle object formatting
            if (typeof text === 'object') {
                contentToCopy = JSON.stringify(text, null, 2);
            }

            await navigator.clipboard.writeText(contentToCopy);

            // Update copy status
            setCopyStatus(prev => ({ ...prev, [type]: true }));

            // Reset after 2 seconds
            setTimeout(() => {
                setCopyStatus(prev => ({ ...prev, [type]: false }));
            }, 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    }, []);

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '75%' }}>
            <Offcanvas.Header closeButton style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
                <Offcanvas.Title style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#1e293b',
                    letterSpacing: '-0.5px'
                }}>
                    📋 Audit Detail
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body style={{ padding: '24px' }}>
                {loading && (
                    <div style={{
                        textAlign: 'center',
                        padding: '48px 24px',
color: '#64748b'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid #f1f5f9',
                            borderTop: '3px solid #3b82f6',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 16px'
                        }}></div>
                        <p style={{ margin: 0, fontSize: '14px' }}>Loading audit details...</p>
                    </div >
                )}
{
    error && (
        <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '16px',
            color: '#dc2626',
            fontSize: '14px',
            marginBottom: '16px'
        }}>
            ❌ {error}
        </div>
    )
}

{
    detail && !loading && (
        <div className="aii-drawer-body">
            {/* User Info Section */}
            <div style={{ marginBottom: '24px' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                }}>
                    <div style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#6366f1',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600'
                    }}>
                        👤
                    </div>
                    <h6 style={{
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#334155',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        User Info
                    </h6>
                </div>
                <div style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '16px',
                    backgroundColor: '#f8fafc'
                }}>
                    {detail.userDetails ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: '#6366f1',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    {detail.userDetails._id?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '600', color: '#1e293b' }}>
                                        {detail.userDetails._id || 'N/A'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                                        {detail.userDetails.profession || 'No profession'}
                                    </div>
                                </div>
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid #e2e8f0'
                            }}>
                                <div>
                                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        deviceType
                                    </div>
                                    <div style={{ fontWeight: '500', color: '#1e293b' }}>
                                        {detail.userDetails.deviceType === 'A' ? 'Android' : (detail.userDetails.deviceType === 'I' ? 'IOS' : 'Not Mentioned')}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Status
                                    </div>
                                    <span className={`aii-badge ${getStatusBadge(detail.progress)}`}>
                                        {detail.progress}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            color: '#64748b'
                        }}>
                            <div style={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: '#94a3b8',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '600'
                            }}>
                                G
                            </div>
                            <span>Guest User</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Status & Timing Section */}
            <div style={{ marginBottom: '24px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#f59e0b',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600'
                        }}>
                            ⏱️
                        </div>
                        <h6 style={{
                            margin: 0,
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#334155',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Status & Timing
                        </h6>
                    </div>
                    <div style={{
                        fontSize: '12px',
                        color: '#64748b',
                        backgroundColor: '#f1f5f9',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: '500'
                    }}>
                        {new Date(detail.createdDate).toLocaleDateString('en-US', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </div>
                </div>

                {detail.anyIssue && (
                    <div style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '13px',
                        marginBottom: '12px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <span style={{ color: '#d97706', fontSize: '14px' }}>⚠️</span>
                            <div style={{ flex: 1 }}>
                                <strong style={{ color: '#92400e' }}>Issue Reported:</strong>
                                <div style={{ color: '#78350f', marginTop: '4px' }}>
                                    {detail.anyIssue}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Prompt Section with Beautiful Styling */}
            <div style={{ marginBottom: '32px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#8b5cf6',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600'
                        }}>
                            💬
                        </div>
                        <h6 style={{
                            margin: 0,
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#334155',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Prompt
                        </h6>
                    </div>
                    <button
                        onClick={() => handleCopy(detail.prompt, 'prompt')}
                        style={{
                            backgroundColor: copyStatus.prompt ? '#10b981' : '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 16px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease',
                            outline: 'none',
                            boxShadow: copyStatus.prompt ? '0 0 0 3px rgba(16, 185, 129, 0.1)' : '0 1px 2px rgba(139, 92, 246, 0.1)',
                            letterSpacing: '0.3px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = copyStatus.prompt ? '#10b981' : '#7c3aed'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = copyStatus.prompt ? '#10b981' : '#8b5cf6'}
                    >
                        {copyStatus.prompt ? (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12" stroke="currentColor" />
                                </svg>
                                Copied!
                            </>
                        ) : (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" />
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" />
                                </svg>
                                Copy Prompt
                            </>
                        )}
                    </button>
                </div>

                {/* Beautiful Prompt Container */}
                <div style={{
                    position: 'relative',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '24px',
                    borderRadius: '16px',
                    fontSize: '14px',
                    border: '2px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                    lineHeight: '1.7',
                    color: '#334155',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}>
                    {/* Decorative corner accents */}
                    <div style={{
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '60px',
                        height: '60px',
                        borderTop: '2px solid #c7d2fe',
                        borderLeft: '2px solid #c7d2fe',
                        borderTopLeftRadius: '14px',
                        pointerEvents: 'none'
                    }}></div>
                    <div style={{
                        position: 'absolute',
                        bottom: '0',
                        right: '0',
                        width: '60px',
                        height: '60px',
                        borderBottom: '2px solid #c7d2fe',
                        borderRight: '2px solid #c7d2fe',
                        borderBottomRightRadius: '14px',
                        pointerEvents: 'none'
                    }}></div>

                    {/* Prompt Content */}
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '1px solid #e2e8f0',
                        marginBottom: '20px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                        <div style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#1e293b',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📋 Audit Detail
                        </div>

                        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, #c7d2fe, transparent)', margin: '16px 0' }}></div>

                        {/* Type to search section */}
                        {/* <div style={{ marginBottom: '20px' }}>
                                        <div style={{
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}>
                                            <span style={{ color: '#8b5cf6' }}>🔍</span> Type to search
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '6px',
                                            marginBottom: '12px'
                                        }}>
                                            {['Dashboards', 'App Update', 'User', 'AI Audit', 'Fruits List', 'Vegetables List', 'Grains List', 'Medical Checkup List', 'First Aid', 'Vaccination', 'Symptom'].map((item, index) => (
                                                <span key={index} style={{
                                                    backgroundColor: '#f1f5f9',
                                                    padding: '4px 10px',
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    color: '#475569',
                                                    border: '1px solid #e2e8f0',
                                                    fontWeight: '500'
                                                }}>
                                                    {item}
                                                </span>
                                            ))}
                                        </div>
                                    </div> */}

                        {/* <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, #c7d2fe, transparent)', margin: '16px 0' }}></div> */}

                        {/* Audit Details section */}
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#1e293b',
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}>
                                📊 Audit Details
                            </div>

                            {/* Existing Conditions */}
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#475569',
                                    marginBottom: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <span style={{ color: '#10b981' }}>✓</span> Existing Conditions:
                                </div>
                                <ul style={{ margin: '0 0 0 20px', padding: 0 }}>
                                    <li style={{ marginBottom: '4px', color: '#64748b' }}>
                                        <strong>Family History:</strong> no
                                    </li>
                                    <li style={{ color: '#64748b' }}>
                                        <strong>Affected Members:</strong> None reported
                                    </li>
                                </ul>
                            </div>

                            {/* Allergies */}
                            <div>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#475569',
                                    marginBottom: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <span style={{ color: '#f59e0b' }}>⚠️</span> Allergies:
                                </div>
                                <ul style={{ margin: '0 0 0 20px', padding: 0 }}>
                                    <li style={{ marginBottom: '4px', color: '#64748b' }}>
                                        <strong>Medications:</strong> No regular medications
                                    </li>
                                    <li style={{ marginBottom: '4px', color: '#64748b' }}>
                                        <strong>Hospitalized Before:</strong> no
                                    </li>
                                    <li style={{ color: '#64748b' }}>
                                        <strong>Surgeries:</strong> no
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, #c7d2fe, transparent)', margin: '16px 0' }}></div>

                        {/* Task section */}
                        <div>
                            <div style={{
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#1e293b',
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}>
                                🎯 Task
                            </div>
                            <div style={{
                                backgroundColor: '#f0f9ff',
                                borderRadius: '8px',
                                padding: '16px',
                                border: '1px solid #bae6fd'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '8px',
                                    fontSize: '13px',
                                    color: '#0369a1'
                                }}>
                                    {[
                                        '1. Summarize overall health status.',
                                        '2. Identify lifestyle-related risks.',
                                        '3. Suggest diet improvements.',
                                        '4. Recommend a realistic fitness routine.',
                                        '5. Provide sleep improvement tips.',
                                        '6. Suggest 5 daily healthy habits.',
                                        '7. Keep the tone "friendly".',
                                        '8. Avoid medical diagnosis. Focus on prevention and wellness only.'
                                    ].map((task, index) => (
                                        <div key={index} style={{
                                            backgroundColor: 'white',
                                            padding: '8px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid #bae6fd',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '6px'
                                        }}>
                                            <span style={{
                                                backgroundColor: '#0ea5e9',
                                                color: 'white',
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                flexShrink: 0
                                            }}>
                                                {index + 1}
                                            </span>
                                            <span>{task}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, #c7d2fe, transparent)', margin: '16px 0' }}></div>

                        {/* Response format */}
                        <div style={{
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px',
                            padding: '12px',
                            border: '1px solid #fcd34d',
                            fontSize: '13px',
                            color: '#92400e'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <span style={{ fontSize: '14px', flexShrink: 0 }}>📝</span>
                                <div>
                                    <strong>Return the response in:</strong>
                                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                        <li>Structured sections with bullet points</li>
                                        <li>Less than 100 words</li>
                                        <li>Should contain emoji's</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Response Insights with Copy */}
            <div style={{ marginBottom: '32px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#10b981',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600'
                        }}>
                            🤖
                        </div>
                        <h6 style={{
                            margin: 0,
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#334155',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            AI Response Insights
                        </h6>
                    </div>
                    <button
                        onClick={() => handleCopy(detail.aiResponseInsights, 'insights')}
                        style={{
                            backgroundColor: copyStatus.insights ? '#10b981' : '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 16px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease',
                            outline: 'none',
                            boxShadow: copyStatus.insights ? '0 0 0 3px rgba(16, 185, 129, 0.1)' : '0 1px 2px rgba(16, 185, 129, 0.1)',
                            letterSpacing: '0.3px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = copyStatus.insights ? '#10b981' : '#059669'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = copyStatus.insights ? '#10b981' : '#10b981'}
                    >
                        {copyStatus.insights ? (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12" stroke="currentColor" />
                                </svg>
                                Copied!
                            </>
                        ) : (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" />
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" />
                                </svg>
                                Copy Insights
                            </>
                        )}
                    </button>
                </div>
                <div style={{
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    border: '2px solid #334155',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Decorative glow */}
                    <div style={{
                        position: 'absolute',
                        top: '-50%',
                        right: '-50%',
                        width: '100%',
                        height: '200%',
                        background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
                        pointerEvents: 'none'
                    }}></div>

                    <div style={{
                        backgroundColor: '#1e293b',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '1px solid #475569',
                        position: 'relative',
                        zIndex: 1
                    }}>
                        <div className="ai-markdown-preview">
                            <ReactMarkdown>
                                {typeof detail.aiResponseInsights === 'object'
                                    ? JSON.stringify(detail.aiResponseInsights, null, 2)
                                    : detail.aiResponseInsights
                                }
                            </ReactMarkdown>
                        </div>
                    </div>
                </div>
            </div>

            {/* Raw Response Section with Copy */}
            {detail.aiRawResponse && (
                <div style={{ marginBottom: '24px' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#ef4444',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: '600'
                            }}>
                                🔧
                            </div>
                            <h6 style={{
                                margin: 0,
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#334155',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Raw Response (Debug)
                            </h6>
                        </div>
                        <button
                            onClick={() => handleCopy(detail.aiRawResponse, 'raw')}
                            style={{
                                backgroundColor: copyStatus.raw ? '#10b981' : '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '8px 16px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s ease',
                                outline: 'none',
                                boxShadow: copyStatus.raw ? '0 0 0 3px rgba(16, 185, 129, 0.1)' : '0 1px 2px rgba(239, 68, 68, 0.1)',
                                letterSpacing: '0.3px'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = copyStatus.raw ? '#10b981' : '#dc2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = copyStatus.raw ? '#10b981' : '#ef4444'}
                        >
                            {copyStatus.raw ? (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                        <polyline points="20 6 9 17 4 12" stroke="currentColor" />
                                    </svg>
                                    Copied!
                                </>
                            ) : (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" />
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" />
                                    </svg>
                                    Copy Raw
                                </>
                            )}
                        </button>
                    </div>
                    <div style={{
                        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '1px solid #475569',
                        maxHeight: '300px',
                        overflowY: 'auto'
                    }}>
                        <pre style={{
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            padding: '0',
                            fontSize: '11px',
                            fontFamily: "'JetBrains Mono', 'SF Mono', Consolas, monospace",
                            lineHeight: '1.4',
                            color: '#94a3b8'
                        }}>
                            {JSON.stringify(detail.aiRawResponse, null, 2)}
                        </pre>
                    </div>
                </div>
            )}

            {/* Cost Analysis Section */}
            <CostAnalysis detail={detail} />

        </div>
    )
}
            </Offcanvas.Body >
    <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .aii-badge {
                    display: inline-block;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .aii-badge-success {
                    background-color: #d1fae5;
                    color: #065f46;
                }
                
                .aii-badge-fail {
                    background-color: #fee2e2;
                    color: #991b1b;
                }
                
                .aii-badge-info {
                    background-color: #dbeafe;
                    color: #1e40af;
                }
                
                .aii-badge-neutral {
                    background-color: #f1f5f9;
                    color: #475569;
                }

                /* AI Markdown Preview Styles */
                .ai-markdown-preview {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    font-size: 14px;
                    line-height: 1.7;
                    color: #e2e8f0;
                }
                .ai-markdown-preview h1,
                .ai-markdown-preview h2,
                .ai-markdown-preview h3,
                .ai-markdown-preview h4,
                .ai-markdown-preview h5,
                .ai-markdown-preview h6 {
                    color: #f1f5f9;
                    margin-top: 16px;
                    margin-bottom: 8px;
                    font-weight: 700;
                    line-height: 1.3;
                }
                .ai-markdown-preview h1 { font-size: 22px; }
                .ai-markdown-preview h2 { font-size: 18px; border-bottom: 1px solid #334155; padding-bottom: 6px; }
                .ai-markdown-preview h3 { font-size: 16px; }
                .ai-markdown-preview h4 { font-size: 14px; }
                .ai-markdown-preview p {
                    margin: 0 0 12px 0;
                    color: #cbd5e1;
                }
                .ai-markdown-preview ul,
                .ai-markdown-preview ol {
                    margin: 0 0 12px 0;
                    padding-left: 24px;
                }
                .ai-markdown-preview li {
                    margin-bottom: 4px;
                    color: #cbd5e1;
                }
                .ai-markdown-preview li::marker {
                    color: #10b981;
                }
                .ai-markdown-preview strong {
                    color: #f8fafc;
                    font-weight: 600;
                }
                .ai-markdown-preview em {
                    color: #94a3b8;
                    font-style: italic;
                }
                .ai-markdown-preview code {
                    background-color: #334155;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
                    font-size: 12px;
                    color: #7dd3fc;
                }
                .ai-markdown-preview pre {
                    background-color: #0f172a;
                    padding: 12px;
                    border-radius: 8px;
                    overflow-x: auto;
                    margin: 8px 0;
                }
                .ai-markdown-preview pre code {
                    background: none;
                    padding: 0;
                }
                .ai-markdown-preview hr {
                    border: none;
                    border-top: 1px solid #334155;
                    margin: 16px 0;
                }
                .ai-markdown-preview blockquote {
                    border-left: 3px solid #10b981;
                    padding-left: 12px;
                    margin: 8px 0;
                    color: #94a3b8;
                }
                .ai-markdown-preview a {
                    color: #38bdf8;
                    text-decoration: underline;
                }
            `}</style>
        </Offcanvas >
    );
};

const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
        case 'completed': return 'aii-badge-success';
        case 'failed': return 'aii-badge-fail';
        case 'in_progress': return 'aii-badge-info';
        case 'started': return 'aii-badge-neutral';
        default: return 'aii-badge-neutral';
    }
};

export default AuditDetailDrawer;
